import { format } from 'date-fns';
import { zonedTimeToUtc, format as tzFormat, utcToZonedTime } from 'date-fns-tz';
import {
  type CurrencyConfig,
  CurrencyPosition,
  CurrencySymbolOrCode,
  PreferenceService,
} from '../Preferences/PreferenceService';
import { ConfigService } from '../Configs/ConfigService';
import { PageConfigService } from '../PageConfigs/PageConfigService';
import currencySymbols from '../MasterData/currencySymbols.json';
import { DateFormats, DateTimeFormats, NumberFormatStyle, TimeFormats, DateOrTime, TimeZone } from './types/enum';
import type { DateTimeFormatsType, DateFormatConfig, FormatPriceOptions } from './types/types';

const DEFAULT_TIMEZONE = 'GMT';
const FALLBACK_MAXIMUM_FRACTION_DIGITS = 20;
const { locale } = PreferenceService.getPreferences();
const DEFAULT_LOCALE = locale || 'en';
const formatNumber = (number: number): string => {
  const { locale } = PreferenceService.getPreferences();
  let formattedNumber = new Intl.NumberFormat(locale || 'th', {
    style: NumberFormatStyle.DECIMAL,
  }).format(number);
  formattedNumber = formattedNumber.replace(/[\u00a0]/g, ' ');
  return formattedNumber;
};
export const hasCurrencySymbolOverride = (currencyCode?: string | null): boolean =>
  !!currencySymbols.find((item) => item.currencyCode === currencyCode);

/**
 * Formats a number as a price string based on locale and currency preferences.
 * @param number - The numeric value to format.
 * @param curr - Currency code override. Falls back to preference currency if null.
 * @param options - Optional formatting options to control rounding behavior.
 * @returns Formatted price string.
 */
const formatPrice = (number: number, curr: string | null = null, options?: FormatPriceOptions): string => {
  const { locale, currency: currencyCode, currencyFormatConfig } = PreferenceService.getPreferences();
  const currency = curr || currencyCode;
  let formattedPrice = '';
  if (currencyFormatConfig && currency && currency in currencyFormatConfig) {
    formattedPrice = customPriceFormat(number, currencyFormatConfig[currency] as CurrencyConfig);
  } else {
    const hasCurrencyDisplayOverride = hasCurrencySymbolOverride(currency);
    formattedPrice = new Intl.NumberFormat(locale || 'th', {
      style: currency ? NumberFormatStyle.CURRENCY : NumberFormatStyle.DECIMAL,
      ...(currency ? { currency } : {}),
      ...(hasCurrencyDisplayOverride ? { currencyDisplay: 'narrowSymbol' as const } : {}),
      ...(options?.skipRounding
        ? { maximumFractionDigits: options.maximumFractionDigits ?? FALLBACK_MAXIMUM_FRACTION_DIGITS }
        : {}),
    }).format(number);
    formattedPrice = formattedPrice.replace(/ /g, ' ');
    // check if price is like "$1000" then produces "$ 1000."
    if (formattedPrice.match(/^(\D)(\d)/gm)) {
      formattedPrice = formattedPrice.replace(formattedPrice[0] || '', `${formattedPrice[0]} `);
    }
  }
  return formattedPrice;
};

const addSeparators = (price: string, thousandSeparator = ',', decimalSeparator = '.') => {
  const [integerPart, decimalPart] = price.split('.');

  const formattedIntegerPart = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  if (decimalPart) {
    return `${formattedIntegerPart}${decimalSeparator}${decimalPart}`;
  }
  return formattedIntegerPart;
};

const checkAndFormat = (priceString: string, type: string) => {
  if (type === 'CEIL') {
    return `${Math.ceil(Number(priceString))}`;
  } else if (type === 'FLOOR') {
    return `${Math.floor(Number(priceString))}`;
  } else if (type === 'ROUND') {
    return `${Math.round(Number(priceString))}`;
  }
  return priceString;
};

const customPriceFormat = (number: number, currency: CurrencyConfig): string => {
  const configs = ConfigService.getConfigs();
  const pageConfigs = PageConfigService.getPageConfigs();

  let priceString = number.toString();
  if (pageConfigs?.priceFormat) {
    priceString = checkAndFormat(priceString, pageConfigs?.priceFormat as string);
  } else if (currency?.roundOff) {
    priceString = `${Math.round(Number(priceString))}`;
  }

  const removeDecimals = pageConfigs.removePriceDecimals ? true : configs.REMOVE_PRICE_TRAILING_ZEROS;
  priceString = Number(priceString).toFixed(
    removeDecimals && Number.isInteger(Number(priceString)) ? 0 : currency.decimals,
  );

  priceString = addSeparators(priceString, currency.thousandSeparator, currency.decimalSeparator) as string;

  const isSymbol = currency.showSymbolOrCode === CurrencySymbolOrCode.SYMBOL;
  const value = isSymbol ? currency.symbol : currency.code;
  const position = isSymbol ? currency.symbolPosition : currency.codePosition;
  priceString = position === CurrencyPosition.BEFORE_PRICE ? `${value} ${priceString}` : `${priceString} ${value}`;

  return priceString;
};

const formatDate = (date: string, dateFormat: DateFormats, locale?: string, convertTimeZone?: boolean): string =>
  formatDateOrTime(date, { date: dateFormat }, undefined, undefined, locale, DateOrTime.DATE_ONLY, convertTimeZone);

const getDDMMYYYY = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'en-In');
  return separator ? dateString.replaceAll('/', separator) : dateString;
};
const getDDMMYYYYv1 = (date: string, config: DateFormatConfig = {}): string => {
  const { separator, convertTimeZone } = config;
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'en-In', convertTimeZone);
  return separator ? dateString.replaceAll('/', separator) : dateString;
};

const getDDMMMYYYY = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.MEDIUM_DATE, 'en-In');
  return separator ? dateString.replaceAll('-', separator) : dateString;
};
const getDDMMMYYYYv1 = (date: string, config: DateFormatConfig = {}): string => {
  const { separator, convertTimeZone } = config;
  const dateString = formatDate(date, DateFormats.MEDIUM_DATE, 'en-In', convertTimeZone);
  return separator ? dateString.replaceAll('-', separator) : dateString;
};

const getMMDDYYYY = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'en-US');
  return separator ? dateString.replaceAll('/', separator) : dateString;
};
const getMMDDYYYYv1 = (date: string, config: DateFormatConfig = {}): string => {
  const { separator, convertTimeZone } = config;
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'en-US', convertTimeZone);
  return separator ? dateString.replaceAll('/', separator) : dateString;
};

const getYYYYMMDD = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'ja-JP');
  return separator ? dateString.replaceAll('/', separator) : dateString;
};
const getYYYYMMDDv1 = (date: string, config: DateFormatConfig = {}): string => {
  const { separator, convertTimeZone } = config;
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'ja-JP', convertTimeZone);
  return separator ? dateString.replaceAll('/', separator) : dateString;
};

const getYYYYMMDDJP = (date: string, convertTimeZone?: boolean): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'ja-JP', convertTimeZone);
  const dateArray = dateString.split('/');

  return `${dateArray[0]}年 ${dateArray[1]}月 ${dateArray[2]}日`;
};

/**
 *
 * @param time contains only time value (NO DATE) Ex. 8:56:42 PM (Correct), 2024-08-22T00:46:29.957+07:00 (Incorrect)
 */
const formatTime = (
  time: string,
  timeFormat: TimeFormats,
  hour12?: boolean,
  targetTZ?: string,
  locale?: string,
): string => formatDateOrTime(`0, ${time}`, { time: timeFormat }, hour12, targetTZ, locale, DateOrTime.TIME_ONLY);

const getHHMM = (time: string, hour12?: boolean) => formatTime(time, TimeFormats.SHORT_TIME, hour12);

const getHHMMSS = (time: string, hour12?: boolean) => formatTime(time, TimeFormats.MEDIUM_TIME, hour12);

// To format date in locale and timezone specific time with internationalization
const formatDateTime = (
  dateTime?: string,
  dateTimeFormats?: DateTimeFormatsType,
  hour12?: boolean,
  targetTZ?: string,
  locale?: string,
  dateOrTime = DateOrTime.DATE_AND_TIME,
): string => formatDateOrTime(dateTime, dateTimeFormats, hour12, targetTZ, locale, dateOrTime);

const formatDateOrTime = (
  dateTime?: string,
  dateTimeFormats?: DateTimeFormatsType,
  hour12?: boolean,
  targetTZ?: string,
  locale?: string,
  dateOrTime = DateOrTime.DATE_AND_TIME,
  convertTimeZone: boolean = true,
): string => {
  const inputDateTime = dateTime ? new Date(dateTime) : new Date();

  const configs = ConfigService.getConfigs();
  const { locale: preferenceLocale } = PreferenceService.getPreferences();
  const tarTZ = targetTZ ?? (configs.NEXT_PUBLIC_TIMEZONE as string);
  const loc = locale ?? (preferenceLocale || (configs.NEXT_PUBLIC_LOCALE as string)) ?? DEFAULT_LOCALE;

  const date = dateTimeFormats?.date ?? DateFormats.SHORT_DATE;
  const time = dateTimeFormats?.time ?? TimeFormats.SHORT_TIME;

  switch (dateOrTime) {
    case DateOrTime.DATE_ONLY:
      // When convertTimeZone=true, then the date undergoes timezone conversion either using current timezone (timezone of wherever this code runs) or Intl.DateTimeFormat().resolvedOptions().timeZone or using DEFAULT_TIMEZONE in case of time related changes.
      // When convertTimeZone=false, then skip the timeZone conversion to keep the original date
      return Intl.DateTimeFormat(loc, {
        ...DateTimeFormats[date || DateFormats.SHORT_DATE],
        ...(convertTimeZone && {
          timeZone: tarTZ || Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      }).format(inputDateTime);

    case DateOrTime.TIME_ONLY:
      return Intl.DateTimeFormat(loc, {
        ...DateTimeFormats[time || TimeFormats.SHORT_TIME],
        ...(convertTimeZone && { timeZone: tarTZ || DEFAULT_TIMEZONE }),
        hour12: hour12 ?? true,
      }).format(inputDateTime);

    // DateOrTime.DATE_AND_TIME
    default:
      return Intl.DateTimeFormat(loc, {
        ...DateTimeFormats[date || DateFormats.SHORT_DATE],
        ...DateTimeFormats[time || TimeFormats.SHORT_TIME],
        ...(convertTimeZone && { timeZone: tarTZ || DEFAULT_TIMEZONE }),
        hour12: hour12 ?? true,
      }).format(inputDateTime);
  }
};

// To format date in custom formats
/**
 * @deprecated Please use formatDateTime, formatDate, or formatTime instead.
 */
const dateTimeToCustomFormat = (inputDate: string, formatStr: string): string => format(new Date(inputDate), formatStr);

const replaceTimeZoneToSpecified = (date: Date, toTimeZone?: TimeZone, fromTimeZone?: TimeZone) => {
  const configs = ConfigService.getConfigs();
  toTimeZone = toTimeZone ? toTimeZone : (configs.NEXT_PUBLIC_TIMEZONE as TimeZone);
  fromTimeZone = fromTimeZone ? fromTimeZone : (Intl.DateTimeFormat().resolvedOptions().timeZone as TimeZone);
  const dateZoned = zonedTimeToUtc(date, fromTimeZone);
  const convertedDate = tzFormat(dateZoned, 'yyyy-MM-dd HH:mm:ss zzz', { timeZone: toTimeZone });
  return convertedDate;
};

const toLocalDate = (date: string | Date) => {
  const { NEXT_PUBLIC_TIMEZONE } = ConfigService.getConfigs();
  const tz = NEXT_PUBLIC_TIMEZONE ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date(utcToZonedTime(date, tz as string));
};

const toUTCDate = (date: Date) => {
  const { NEXT_PUBLIC_TIMEZONE } = ConfigService.getConfigs();
  const tz = NEXT_PUBLIC_TIMEZONE ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return zonedTimeToUtc(date, tz as string);
};

export {
  formatNumber,
  formatPrice,
  customPriceFormat,
  formatDate,
  getDDMMYYYY,
  getDDMMMYYYY,
  getMMDDYYYY,
  getYYYYMMDD,
  formatTime,
  getHHMM,
  getHHMMSS,
  formatDateTime,
  dateTimeToCustomFormat,
  replaceTimeZoneToSpecified,
  DateFormats,
  TimeFormats,
  DateOrTime,
  TimeZone,
  toLocalDate,
  toUTCDate,
  getYYYYMMDDJP,
  getDDMMMYYYYv1,
  getDDMMYYYYv1,
  getMMDDYYYYv1,
  getYYYYMMDDv1,
};
